// Is this page being answered by the build it belongs to?
//
// Two different questions wear the same clothes, and the answer depends on where the page is
// running:
//
// - **Deployed**, the risk is a browser holding the previous deploy's JavaScript while the
//   service has moved on. Both halves carry a commit, and comparing them catches it.
// - **Locally**, the risk is the page talking to *another checkout's* backend -- the 28 Aug
//   accident, where a hardcoded proxy target served one worktree's page another worktree's
//   payload. Every number was well-formed and every number was from the wrong branch.
//
// The local comparison is on the checkout rather than the commit, because the commit answers
// the wrong question there. Worktrees sit on the same commit constantly (three of eleven did
// on 28 Aug), so equal commits do not mean the same code; and a commit baked into a running
// dev server stops being true the moment you commit again, so unequal commits do not mean a
// mismatch either. The directory a checkout lives in is stable across both.
//
// Split out of StaleBuildNotice so it can be tested (`npm test`) rather than only observed --
// this warning exists for a rare failure, and a rare failure is where a silent regression
// hides. Type-only imports, which is what keeps it runnable under `node --test`.

import type { Build } from '../api/types';

export interface PageBuild {
  commit: string;
  checkout: string;
}

// `stale-bundle` is the deployed case: this tab's JavaScript is older than the service's.
// `other-checkout` is the local one: a different working copy is answering.
export type BuildMatch = 'match' | 'stale-bundle' | 'other-checkout';

export function compareBuild(page: PageBuild, api: Build | undefined): BuildMatch {
  // Both sides name a checkout, so this is local development and that is the comparison.
  if (page.checkout && api?.checkout) {
    return page.checkout === api.checkout ? 'match' : 'other-checkout';
  }

  // Half a pair cannot catch a mismatch. Nothing stamped means nothing to say, which is the
  // honest answer for a build made outside git with no deployment behind it either.
  if (!page.commit || !api?.commit) return 'match';

  return page.commit === api.commit ? 'match' : 'stale-bundle';
}
